import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import T from 'components/T';

const MultilineText = ({
  autoFocus,
  disabled,
  input,
  label,
  meta: { touched, error, warning },
  placeholder,
  onPaste,
  dateformat,
  remainingCharacterCount,
  maxLength,
  'data-testid': dataTestId,
}) => {
  // A validation error is a translation key, or `{ message, ...params }` with
  // the params for that translation.
  const { message: errorKey, ...errorParams } =
    error && typeof error !== 'string' ? error : { message: error };
  const invalid = Boolean(touched && errorKey);
  const warn = Boolean(touched && !errorKey && warning);

  return (
    <M.TextArea
      id={input.name ? input.name.replace('.', '-') : 'multi-line-text-field'}
      name={input.name}
      value={input.value}
      // redux-form reads the value from the event, and the handlers some
      // fields pass as `onChange` read `event.target.value`.
      onChange={event => input.onChange(event)}
      onBlur={event => input.onBlur(event)}
      onFocus={input.onFocus}
      // Mastodon's TextArea takes `label` and maps it to Carbon's `labelText`.
      label={label ? <T value={label} dateformat={dateformat} /> : undefined}
      placeholder={i18n.getStr(placeholder || label)}
      autoFocus={autoFocus}
      disabled={disabled}
      onPaste={onPaste}
      rows={6}
      // `null` means no limit; leaving it out keeps the 1000 default.
      maxLength={maxLength ?? undefined}
      data-testid={dataTestId || undefined}
      invalid={invalid}
      invalidText={
        invalid ? <T value={errorKey} {...errorParams} /> : undefined
      }
      warn={warn}
      warnText={warn ? warning : undefined}
      helperText={
        remainingCharacterCount >= 0
          ? i18n.getStr(
              'components.MultilineText.remainingCharacterCount',
              remainingCharacterCount.toString(),
            )
          : undefined
      }
    />
  );
};

MultilineText.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  input: PropTypes.object,
  label: PropTypes.string,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  onPaste: PropTypes.func,
  dateformat: PropTypes.string,
  remainingCharacterCount: PropTypes.number,
  maxLength: PropTypes.number,
  'data-testid': PropTypes.string,
};

MultilineText.defaultProps = {
  autoFocus: false,
  disabled: false,
  input: {},
  label: '',
  meta: {},
  placeholder: '',
  onPaste: () => {},
  dateformat: '',
  remainingCharacterCount: -1,
  maxLength: 1000,
  'data-testid': '',
};

export default i18n.renderTranslation()(MultilineText);
